"use client";

import {
  Heart,
  Send,
  Check,
  X,
  Calendar,
  User,
  Loader2,
  Gift,
  Cake,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  MessageSquare,
  Clock,
  Award,
  PartyPopper,
  UtensilsCrossed,
  History,
  BarChart3,
  UserX,
  CheckCircle,
  Filter,
  CheckSquare,
  Download,
  FileSpreadsheet,
  Settings,
  AlertTriangle,
  Shield,
  Bell,
} from "lucide-react";
import React, { useState, useMemo, useEffect } from "react";
import { format, addDays, parseISO } from "date-fns";
import { toZonedTime, format as formatTz } from "date-fns-tz";
import Link from "next/link";

// Format phone number to (xxx) xxx-xxxx
const formatPhoneNumber = (phone: string): string => {
  if (!phone) return "";
  // Remove all non-digit characters
  const digits = phone.replace(/\D/g, "");
  // Handle US numbers with country code
  const nationalNumber = digits.length === 11 && digits.startsWith("1")
    ? digits.slice(1)
    : digits;
  // Format if we have 10 digits
  if (nationalNumber.length === 10) {
    return `(${nationalNumber.slice(0, 3)}) ${nationalNumber.slice(3, 6)}-${nationalNumber.slice(6)}`;
  }
  return phone; // Return original if can't format
};

// US State to timezone mapping
const STATE_TIMEZONES: Record<string, { timezone: string; abbrev: string }> = {
  // Eastern Time
  CT: { timezone: "America/New_York", abbrev: "ET" },
  DE: { timezone: "America/New_York", abbrev: "ET" },
  DC: { timezone: "America/New_York", abbrev: "ET" },
  FL: { timezone: "America/New_York", abbrev: "ET" }, // Most of FL is Eastern
  GA: { timezone: "America/New_York", abbrev: "ET" },
  IN: { timezone: "America/Indiana/Indianapolis", abbrev: "ET" }, // Most of IN
  KY: { timezone: "America/New_York", abbrev: "ET" }, // Eastern KY
  ME: { timezone: "America/New_York", abbrev: "ET" },
  MD: { timezone: "America/New_York", abbrev: "ET" },
  MA: { timezone: "America/New_York", abbrev: "ET" },
  MI: { timezone: "America/New_York", abbrev: "ET" }, // Most of MI
  NH: { timezone: "America/New_York", abbrev: "ET" },
  NJ: { timezone: "America/New_York", abbrev: "ET" },
  NY: { timezone: "America/New_York", abbrev: "ET" },
  NC: { timezone: "America/New_York", abbrev: "ET" },
  OH: { timezone: "America/New_York", abbrev: "ET" },
  PA: { timezone: "America/New_York", abbrev: "ET" },
  RI: { timezone: "America/New_York", abbrev: "ET" },
  SC: { timezone: "America/New_York", abbrev: "ET" },
  VT: { timezone: "America/New_York", abbrev: "ET" },
  VA: { timezone: "America/New_York", abbrev: "ET" },
  WV: { timezone: "America/New_York", abbrev: "ET" },
  // Central Time
  AL: { timezone: "America/Chicago", abbrev: "CT" },
  AR: { timezone: "America/Chicago", abbrev: "CT" },
  IL: { timezone: "America/Chicago", abbrev: "CT" },
  IA: { timezone: "America/Chicago", abbrev: "CT" },
  KS: { timezone: "America/Chicago", abbrev: "CT" }, // Most of KS
  LA: { timezone: "America/Chicago", abbrev: "CT" },
  MN: { timezone: "America/Chicago", abbrev: "CT" },
  MS: { timezone: "America/Chicago", abbrev: "CT" },
  MO: { timezone: "America/Chicago", abbrev: "CT" },
  NE: { timezone: "America/Chicago", abbrev: "CT" }, // Most of NE
  ND: { timezone: "America/Chicago", abbrev: "CT" }, // Most of ND
  OK: { timezone: "America/Chicago", abbrev: "CT" },
  SD: { timezone: "America/Chicago", abbrev: "CT" }, // Most of SD
  TN: { timezone: "America/Chicago", abbrev: "CT" }, // Most of TN
  TX: { timezone: "America/Chicago", abbrev: "CT" }, // Most of TX
  WI: { timezone: "America/Chicago", abbrev: "CT" },
  // Mountain Time
  AZ: { timezone: "America/Phoenix", abbrev: "MT" }, // No DST
  CO: { timezone: "America/Denver", abbrev: "MT" },
  ID: { timezone: "America/Boise", abbrev: "MT" }, // Most of ID
  MT: { timezone: "America/Denver", abbrev: "MT" },
  NM: { timezone: "America/Denver", abbrev: "MT" },
  UT: { timezone: "America/Denver", abbrev: "MT" },
  WY: { timezone: "America/Denver", abbrev: "MT" },
  NV: { timezone: "America/Los_Angeles", abbrev: "PT" }, // Most of NV is actually Pacific
  // Pacific Time
  CA: { timezone: "America/Los_Angeles", abbrev: "PT" },
  OR: { timezone: "America/Los_Angeles", abbrev: "PT" },
  WA: { timezone: "America/Los_Angeles", abbrev: "PT" },
  // Alaska & Hawaii
  AK: { timezone: "America/Anchorage", abbrev: "AKT" },
  HI: { timezone: "Pacific/Honolulu", abbrev: "HST" },
  // Territories
  PR: { timezone: "America/Puerto_Rico", abbrev: "AST" },
  VI: { timezone: "America/Virgin", abbrev: "AST" },
  GU: { timezone: "Pacific/Guam", abbrev: "ChST" },
};

// Get timezone info for a US state
const getTimezoneForState = (state: string): { timezone: string; abbrev: string } | null => {
  if (!state) return null;
  const upperState = state.toUpperCase().trim();
  return STATE_TIMEZONES[upperState] || null;
};

// Convert scheduled time to client's local time based on their state
const getClientLocalTime = (
  scheduledDate: string,
  scheduledTime: string,
  clientState: string,
  serverTimezone: string = "America/New_York" // Assume server stores in ET
): { localTime: string; abbrev: string } | null => {
  if (!clientState || !scheduledDate || !scheduledTime) return null;

  const tzInfo = getTimezoneForState(clientState);
  if (!tzInfo) return null;

  try {
    // Create a date string and parse it
    const dateTimeStr = `${scheduledDate}T${scheduledTime}:00`;
    const serverDate = new Date(dateTimeStr);

    // Convert to client's timezone
    const clientDate = toZonedTime(serverDate, tzInfo.timezone);
    const localTime = formatTz(clientDate, "h:mm a", { timeZone: tzInfo.timezone });

    return { localTime, abbrev: tzInfo.abbrev };
  } catch (error) {
    console.error("Error converting timezone:", error);
    return null;
  }
};

import { Button } from "@/app/_module/components/ui/button";
import { A2PGateNotice } from "@/app/_module/components/A2PGateNotice";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/app/_module/components/ui/card";
import { Badge } from "@/app/_module/components/ui/badge";
import { Skeleton } from "@/app/_module/components/ui/skeleton";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/app/_module/components/ui/collapsible";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/app/_module/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/app/_module/components/ui/table";
import { Alert, AlertDescription } from "@/app/_module/components/ui/alert";
import { Checkbox } from "@/app/_module/components/ui/checkbox";
import { Label } from "@/app/_module/components/ui/label";
import { Input } from "@/app/_module/components/ui/input";
import { Textarea } from "@/app/_module/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/app/_module/components/ui/select";
import {
  Dialog,
  ResponsiveDialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/app/_module/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/app/_module/components/ui/dropdown-menu";
import { DataPagination, type PaginationMeta } from "@/app/_module/components/ui/data-pagination";
import { useToast } from "@/app/_module/hooks/use-toast";
import {
  useSentimentalSchedules,
  useSentimentalLogs,
  useApproveSchedule,
  useRejectSchedule,
  useApproveAll,
  useUpdateScheduleMessage,
  useUpcomingOccasions,
  useSentimentalSettings,
} from "@/app/_module/hooks/useSentimentals";
import type { SentimentalSchedule, SentimentalLog, OccasionType, UpcomingOccasion, ScheduleStatus, MessageStatus } from "@/app/_module/services/sentimentalsService";

import { SendManualSentimentalDialog } from "./SendManualSentimentalDialog";
import { a2pService } from "@/app/_module/services/a2pService";
import { useRouter } from "next/navigation";

// Occasion icons mapping (support both 'new_year' and 'newyear' formats)
const OCCASION_ICONS: Record<string, React.ElementType> = {
  birthday: Cake,
  policy_anniversary: Award,
  christmas: Gift,
  thanksgiving: UtensilsCrossed,
  new_year: PartyPopper,
  newyear: PartyPopper,
};

// Occasion labels (support both 'new_year' and 'newyear' formats)
const OCCASION_LABELS: Record<string, string> = {
  birthday: "Birthday",
  policy_anniversary: "Policy Anniversary",
  christmas: "Christmas",
  thanksgiving: "Thanksgiving",
  new_year: "New Year",
  newyear: "New Year",
};

// Occasion colors (support both 'new_year' and 'newyear' formats)
const OCCASION_COLORS: Record<string, string> = {
  birthday: "bg-pink-100 text-pink-800 border-pink-200",
  policy_anniversary: "bg-blue-100 text-blue-800 border-blue-200",
  christmas: "bg-red-100 text-red-800 border-red-200",
  thanksgiving: "bg-orange-100 text-orange-800 border-orange-200",
  new_year: "bg-purple-100 text-purple-800 border-purple-200",
  newyear: "bg-purple-100 text-purple-800 border-purple-200",
};

const OCCASION_TYPES: { value: OccasionType | "all"; label: string; color: string }[] = [
  { value: "all", label: "All Occasions", color: "bg-gray-100 text-gray-800" },
  { value: "birthday", label: "Birthday", color: "bg-pink-100 text-pink-800" },
  { value: "christmas", label: "Christmas", color: "bg-red-100 text-red-800" },
  { value: "thanksgiving", label: "Thanksgiving", color: "bg-orange-100 text-orange-800" },
  { value: "new_year", label: "New Year", color: "bg-blue-100 text-blue-800" },
];

export const SentimentalsSection = function() {
  const { toast } = useToast();
  const router = useRouter();

  // Main expanded state - starts minimized
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState("pending");
  const [showManualDialog, setShowManualDialog] = useState(false);

  // A2P verification state
  const [a2pStatus, setA2pStatus] = useState<{ canSend: boolean; reason?: string } | null>(null);
  const [checkingA2P, setCheckingA2P] = useState(true);

  // Filters
  const [occasionFilter, setOccasionFilter] = useState<OccasionType | "all">("all");

  // Pagination
  const [schedulePage, setSchedulePage] = useState(1);
  const [logsPage, setLogsPage] = useState(1);
  const [logsPageSize, setLogsPageSize] = useState(25);

  // Edit dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState<SentimentalSchedule | null>(null);
  const [editedMessage, setEditedMessage] = useState("");

  // Bulk selection
  const [selectedScheduleIds, setSelectedScheduleIds] = useState<Set<string>>(new Set());
  const [approvingId, setApprovingId] = useState<string | null>(null);
  const [rejectingId, setRejectingId] = useState<string | null>(null);

  // Fetch settings
  const { data: settings, isLoading: isLoadingSettings } = useSentimentalSettings();

  // Check A2P verification status on mount
  useEffect(() => {
    const checkA2PStatus = async () => {
      try {
        setCheckingA2P(true);
        const status = await a2pService.canSendSMS();
        setA2pStatus(status);
      } catch (error) {
        setA2pStatus({ canSend: false, reason: 'Failed to check A2P verification status' });
      } finally {
        setCheckingA2P(false);
      }
    };
    checkA2PStatus();
  }, []);

  // Fetch schedules
  const { data: schedulesData, isLoading: isLoadingSchedules } = useSentimentalSchedules({
    occasionType: occasionFilter !== "all" ? occasionFilter : undefined,
    page: schedulePage,
    limit: 25,
  });

  // Fetch logs
  const { data: logsData, isLoading: isLoadingLogs } = useSentimentalLogs({
    occasionType: occasionFilter !== "all" ? occasionFilter : undefined,
    page: logsPage,
    limit: logsPageSize,
  });

  // Fetch upcoming occasions
  const { data: upcomingOccasions, isLoading: isLoadingUpcoming } = useUpcomingOccasions(30);

  // Mutations
  const approveMutation = useApproveSchedule();
  const rejectMutation = useRejectSchedule();
  const approveAllMutation = useApproveAll();
  const updateMessageMutation = useUpdateScheduleMessage();

  const schedules = schedulesData || [];
  const logs = logsData?.items || [];
  const logsMeta = logsData?.meta;

  // Pagination meta for logs
  const logsPaginationMeta = useMemo((): PaginationMeta | undefined => {
    if (!logsMeta) return undefined;
    return {
      totalItems: logsMeta.totalItems,
      page: logsMeta.page,
      limit: logsMeta.limit,
      totalPages: logsMeta.totalPages,
      itemCount: logsMeta.itemCount,
      hasNextPage: logsMeta.page < logsMeta.totalPages,
      hasPreviousPage: logsMeta.page > 1,
    };
  }, [logsMeta]);

  // Get pending schedules - only show from today onwards (not past dates)
  const pendingSchedules = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Start of today

    return schedules.filter((s) => {
      // Must have pending status
      if (s.status !== "pending" && s.status !== "pending_approval") {
        return false;
      }
      // Must be today or future date
      const scheduleDate = parseISO(s.scheduledDate);
      return scheduleDate >= today;
    });
  }, [schedules]);

  // Get upcoming schedules (next 30 days)
  const upcomingSchedules = useMemo(() => {
    const today = new Date();
    const thirtyDaysFromNow = addDays(today, 30);
    return schedules.filter((s) => {
      const scheduleDate = parseISO(s.scheduledDate);
      return scheduleDate > today && scheduleDate <= thirtyDaysFromNow;
    });
  }, [schedules]);

  const pendingCount = pendingSchedules.length;
  const upcomingCount = upcomingOccasions?.length || 0;

  const handleApprove = async (id: string) => {
    setApprovingId(id);
    try {
      await approveMutation.mutateAsync(id);
    } finally {
      setApprovingId(null);
    }
  };

  const handleReject = async (id: string) => {
    setRejectingId(id);
    try {
      await rejectMutation.mutateAsync(id);
    } finally {
      setRejectingId(null);
    }
  };

  const handleApproveAll = async () => {
    const today = format(new Date(), "yyyy-MM-dd");
    try {
      await approveAllMutation.mutateAsync(today);
    } catch (error) {
      console.error("Failed to approve all:", error);
    }
  };

  const handleEdit = (schedule: SentimentalSchedule) => {
    setSelectedSchedule(schedule);
    setEditedMessage(schedule.message);
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedSchedule) return;
    try {
      await updateMessageMutation.mutateAsync({
        scheduleId: selectedSchedule.id,
        message: editedMessage,
      });
      setEditDialogOpen(false);
      setSelectedSchedule(null);
    } catch (error) {
      console.error("Failed to update message:", error);
    }
  };

  // Bulk selection
  const toggleScheduleSelection = (id: string) => {
    setSelectedScheduleIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const selectAllSchedules = () => {
    setSelectedScheduleIds(new Set(pendingSchedules.map(s => s.id)));
  };

  const deselectAllSchedules = () => {
    setSelectedScheduleIds(new Set());
  };

  // Export CSV
  const exportToCSV = () => {
    const headers = ["Lead Name", "Phone", "Occasion", "Message", "Status", "Sent At"];
    const rows = logs.map(log => [
      log.leadName,
      log.leadPhone,
      log.occasionType,
      log.message.replace(/"/g, '""'),
      log.status,
      format(parseISO(log.sentAt), "yyyy-MM-dd HH:mm:ss"),
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `sentimentals-history-${format(new Date(), "yyyy-MM-dd")}.csv`;
    link.click();

    toast({
      title: "Export Complete",
      description: "History exported to CSV successfully",
    });
  };

  const getOccasionBadgeColor = (occasionType: string): string => {
    return OCCASION_COLORS[occasionType] || "bg-gray-100 text-gray-800";
  };

  const getStatusBadge = (status: ScheduleStatus | MessageStatus) => {
    const statusStyles: Record<string, { label: string; className: string }> = {
      pending: { label: "Pending", className: "bg-yellow-100 text-yellow-800 border-yellow-200" },
      pending_approval: { label: "Pending", className: "bg-yellow-100 text-yellow-800 border-yellow-200" },
      approved: { label: "Approved", className: "bg-blue-100 text-blue-800 border-blue-200" },
      rejected: { label: "Rejected", className: "bg-gray-100 text-gray-800 border-gray-200" },
      sent: { label: "Sent", className: "bg-green-100 text-green-800 border-green-200" },
      failed: { label: "Failed", className: "bg-red-100 text-red-800 border-red-200" },
      success: { label: "Success", className: "bg-green-100 text-green-800 border-green-200" },
    };
    const config = statusStyles[status] || { label: status, className: "bg-gray-100 text-gray-800" };
    return <Badge className={`text-xs ${config.className}`}>{config.label}</Badge>;
  };

  // Loading state
  if (isLoadingSettings) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Heart className="h-5 w-5 text-pink-500" />
            <Skeleton className="h-6 w-32" />
          </div>
        </CardHeader>
      </Card>
    );
  }

  return (
    <>
      <Card className="border-pink-200 bg-gradient-to-br from-pink-50/50 to-white" data-tour="sentimentals-section">
        {/* Collapsed Header - Always visible */}
        <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
          <CollapsibleTrigger asChild>
            <CardHeader className="pb-3 cursor-pointer hover:bg-pink-50/50 transition-colors">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-pink-100 rounded-lg">
                    <Heart className="h-5 w-5 text-pink-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      Sentimentals
                      {pendingCount > 0 && (
                        <Badge variant="secondary" className="bg-pink-100 text-pink-700">
                          {pendingCount} pending
                        </Badge>
                      )}
                    </CardTitle>
                    <CardDescription>
                      Automated birthday & holiday messages
                    </CardDescription>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {!isExpanded && (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowManualDialog(true);
                        }}
                        className="gap-1"
                      >
                        <Send className="h-3.5 w-3.5" />
                        Send Manual
                      </Button>
                    </>
                  )}
                  {isExpanded ? (
                    <ChevronUp className="h-5 w-5 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="h-5 w-5 text-muted-foreground" />
                  )}
                </div>
              </div>
            </CardHeader>
          </CollapsibleTrigger>

          <CollapsibleContent>
            <CardContent className="pt-0 space-y-4">
              {/* A2P Verification Warning */}
              {!checkingA2P && !a2pStatus?.canSend && (
                <A2PGateNotice
                  stage={(a2pStatus as any)?.stage || 'required'}
                  reason={a2pStatus?.reason}
                  onAction={() => router.push('/settings?tab=a2p-verification')}
                />
              )}

              {/* Settings Status */}
              {!settings?.enabled && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-start gap-3">
                  <Clock className="h-5 w-5 text-yellow-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-yellow-800">Sentimentals Disabled</p>
                    <p className="text-xs text-yellow-700 mt-0.5">
                      Enable automated messages in{" "}
                      <Link href="/settings?tab=sms-settings&smsTab=sentimentals" className="underline hover:no-underline">
                        Settings
                      </Link>
                      {" "}to start sending birthday and holiday greetings.
                    </p>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setShowManualDialog(true)}
                    disabled={!a2pStatus?.canSend}
                    className="gap-1"
                    title={!a2pStatus?.canSend ? "Complete A2P verification to send messages" : undefined}
                  >
                    <Send className="h-3.5 w-3.5" />
                    Send Manual
                  </Button>
                </div>
                <Link href="/settings?tab=sms-settings&smsTab=sentimentals">
                  <Button size="sm" variant="ghost" className="gap-1">
                    <Settings className="h-3.5 w-3.5" />
                    Settings
                  </Button>
                </Link>
              </div>

              {/* Filters */}
              <div className="flex gap-4 p-3 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <Label htmlFor="occasionFilter" className="text-xs">Occasion Type</Label>
                  <Select
                    value={occasionFilter}
                    onValueChange={(value) => setOccasionFilter(value as OccasionType | "all")}
                  >
                    <SelectTrigger id="occasionFilter" className="h-8 text-sm">
                      <SelectValue placeholder="All Occasions" />
                    </SelectTrigger>
                    <SelectContent>
                      {OCCASION_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Tabs */}
              <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="pending" className="text-xs">
                    <Clock className="h-3.5 w-3.5 mr-1" />
                    Pending ({pendingCount})
                  </TabsTrigger>
                  <TabsTrigger value="upcoming" className="text-xs">
                    <Calendar className="h-3.5 w-3.5 mr-1" />
                    Upcoming ({upcomingCount})
                  </TabsTrigger>
                  <TabsTrigger value="history" className="text-xs">
                    <History className="h-3.5 w-3.5 mr-1" />
                    History
                  </TabsTrigger>
                  <TabsTrigger value="analytics" className="text-xs">
                    <BarChart3 className="h-3.5 w-3.5 mr-1" />
                    Stats
                  </TabsTrigger>
                </TabsList>

                {/* Pending Tab */}
                <TabsContent value="pending" className="space-y-3">
                  {isLoadingSchedules ? (
                    <div className="space-y-3">
                      {[1, 2, 3].map((i) => (
                        <Skeleton key={i} className="h-24 w-full" />
                      ))}
                    </div>
                  ) : pendingCount === 0 ? (
                    <Alert>
                      <CheckCircle className="h-4 w-4" />
                      <AlertDescription>
                        No pending approvals. All messages are approved or there are no scheduled messages.
                      </AlertDescription>
                    </Alert>
                  ) : (
                    <>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-muted-foreground">
                          {selectedScheduleIds.size > 0 ? `${selectedScheduleIds.size} selected` : `${pendingCount} messages`}
                        </span>
                        <div className="flex gap-2">
                          {selectedScheduleIds.size > 0 ? (
                            <Button size="sm" variant="outline" onClick={deselectAllSchedules}>
                              Deselect All
                            </Button>
                          ) : (
                            <Button size="sm" variant="outline" onClick={selectAllSchedules}>
                              Select All
                            </Button>
                          )}
                          {pendingCount > 0 && (
                            <Button
                              size="sm"
                              onClick={handleApproveAll}
                              disabled={approveAllMutation.isPending || !a2pStatus?.canSend}
                              title={!a2pStatus?.canSend ? "Complete A2P verification to approve messages" : undefined}
                            >
                              <CheckSquare className="h-3.5 w-3.5 mr-1" />
                              Approve All
                            </Button>
                          )}
                        </div>
                      </div>
                      <div className="space-y-3 max-h-[400px] overflow-y-auto">
                        {pendingSchedules.map((schedule) => (
                          <Card key={schedule.id} className="border">
                            <CardContent className="p-4">
                              <div className="flex items-start gap-3">
                                <Checkbox
                                  checked={selectedScheduleIds.has(schedule.id)}
                                  onCheckedChange={() => toggleScheduleSelection(schedule.id)}
                                />
                                <div className="flex-1 space-y-2">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-medium text-sm">
                                      {schedule.leadName && schedule.leadName !== "Unknown"
                                        ? schedule.leadName
                                        : formatPhoneNumber(schedule.leadPhone)}
                                    </span>
                                    <Badge className={`text-xs ${getOccasionBadgeColor(schedule.occasionType)}`}>
                                      {OCCASION_LABELS[schedule.occasionType] || schedule.occasionType}
                                    </Badge>
                                    {getStatusBadge(schedule.status)}
                                  </div>
                                  <p className="text-xs text-muted-foreground">
                                    {schedule.leadName && schedule.leadName !== "Unknown" && (
                                      <>{formatPhoneNumber(schedule.leadPhone)} | </>
                                    )}
                                    {schedule.occasionDate}
                                  </p>
                                  <div className="p-2 bg-gray-50 rounded text-sm">
                                    <p className="line-clamp-2">{schedule.message}</p>
                                  </div>
                                  <div className="text-xs text-muted-foreground space-y-0.5">
                                    <p>
                                      Scheduled: {schedule.scheduledDate} at {schedule.scheduledTime}
                                    </p>
                                    {schedule.leadState && (() => {
                                      const clientTime = getClientLocalTime(
                                        schedule.scheduledDate,
                                        schedule.scheduledTime,
                                        schedule.leadState
                                      );
                                      return clientTime ? (
                                        <p className="text-blue-600">
                                          Client&apos;s local time: {clientTime.localTime} {clientTime.abbrev}
                                          {schedule.leadState && <span className="text-muted-foreground"> ({schedule.leadState})</span>}
                                        </p>
                                      ) : null;
                                    })()}
                                  </div>
                                </div>
                              </div>
                              <div className="flex justify-end gap-2 mt-3">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleEdit(schedule)}
                                  className="h-7 text-xs"
                                >
                                  Edit
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleReject(schedule.id)}
                                  disabled={rejectingId === schedule.id}
                                  className="h-7 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                                >
                                  {rejectingId === schedule.id ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                  ) : (
                                    <X className="h-3 w-3 mr-1" />
                                  )}
                                  Skip
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={() => handleApprove(schedule.id)}
                                  disabled={approvingId === schedule.id || !a2pStatus?.canSend}
                                  className="h-7 text-xs bg-green-600 hover:bg-green-700"
                                  title={!a2pStatus?.canSend ? "Complete A2P verification to approve messages" : undefined}
                                >
                                  {approvingId === schedule.id ? (
                                    <Loader2 className="h-3 w-3 animate-spin mr-1" />
                                  ) : (
                                    <Check className="h-3 w-3 mr-1" />
                                  )}
                                  Approve
                                </Button>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    </>
                  )}
                </TabsContent>

                {/* Upcoming Tab */}
                <TabsContent value="upcoming" className="space-y-3">
                  {isLoadingUpcoming ? (
                    <div className="space-y-2">
                      {[1, 2, 3].map((i) => (
                        <Skeleton key={i} className="h-16 w-full" />
                      ))}
                    </div>
                  ) : upcomingCount === 0 ? (
                    <Alert>
                      <Calendar className="h-4 w-4" />
                      <AlertDescription>
                        No upcoming occasions in the next 30 days.
                      </AlertDescription>
                    </Alert>
                  ) : (
                    <div className="space-y-2 max-h-[400px] overflow-y-auto">
                      {upcomingOccasions?.map((occasion) => {
                        const occasionType = occasion.occasion.toLowerCase().replace(/ /g, '_') as OccasionType;
                        const Icon = OCCASION_ICONS[occasionType] || Heart;
                        const colorClass = OCCASION_COLORS[occasionType] || "bg-gray-100 text-gray-800";

                        return (
                          <Link
                            key={occasion.id}
                            href={`/leads/${occasion.leadId}`}
                            className="flex items-center justify-between p-3 bg-white rounded-lg border hover:shadow-md hover:border-blue-200 transition-all cursor-pointer"
                          >
                            <div className="flex items-center gap-3">
                              <div className={`p-2 rounded-lg ${colorClass.split(' ')[0]}`}>
                                <Icon className={`h-4 w-4 ${colorClass.split(' ')[1]}`} />
                              </div>
                              <div>
                                <div className="font-medium text-sm hover:text-blue-600">{occasion.leadName}</div>
                                <div className="text-xs text-muted-foreground">
                                  {occasion.occasion} - {format(new Date(occasion.date), "MMM d, yyyy")}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {occasion.daysAway === 1 && (
                                <Badge className="text-xs bg-amber-100 text-amber-800 border-amber-200">
                                  <Bell className="h-3 w-3 mr-1" />
                                  Tomorrow
                                </Badge>
                              )}
                              {occasion.daysAway !== 1 && (
                                <Badge
                                  variant="outline"
                                  className={`text-xs ${occasion.daysAway === 0 ? "bg-green-100 text-green-800 border-green-200" : ""}`}
                                >
                                  {occasion.daysAway === 0 ? "Today!" : `${occasion.daysAway} days`}
                                </Badge>
                              )}
                              <ChevronRight className="h-4 w-4 text-muted-foreground" />
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </TabsContent>

                {/* History Tab */}
                <TabsContent value="history" className="space-y-3">
                  <div className="flex justify-end">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm">
                          <Download className="h-3.5 w-3.5 mr-1" />
                          Export
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={exportToCSV}>
                          <FileSpreadsheet className="h-4 w-4 mr-2" />
                          Export to CSV
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  {isLoadingLogs ? (
                    <Skeleton className="h-48 w-full" />
                  ) : logs.length === 0 ? (
                    <Alert>
                      <History className="h-4 w-4" />
                      <AlertDescription>
                        No message history found. Approved messages will appear here after they are sent.
                      </AlertDescription>
                    </Alert>
                  ) : (
                    <>
                      <div className="max-h-[400px] overflow-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="text-xs">Lead</TableHead>
                              <TableHead className="text-xs">Occasion</TableHead>
                              <TableHead className="text-xs">Status</TableHead>
                              <TableHead className="text-xs">Sent</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {logs.map((log) => (
                              <TableRow key={log.id}>
                                <TableCell>
                                  <div className="text-sm font-medium">
                                    {log.leadName && log.leadName !== "Unknown"
                                      ? log.leadName
                                      : formatPhoneNumber(log.leadPhone)}
                                  </div>
                                  {log.leadName && log.leadName !== "Unknown" && (
                                    <div className="text-xs text-muted-foreground">{formatPhoneNumber(log.leadPhone)}</div>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Badge className={`text-xs ${getOccasionBadgeColor(log.occasionType)}`}>
                                    {OCCASION_LABELS[log.occasionType] || log.occasionType}
                                  </Badge>
                                </TableCell>
                                <TableCell>{getStatusBadge(log.status)}</TableCell>
                                <TableCell className="text-xs">
                                  {format(parseISO(log.sentAt), "MMM d, h:mm a")}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                      {logsPaginationMeta && (
                        <DataPagination
                          meta={logsPaginationMeta}
                          onPageChange={setLogsPage}
                          onPageSizeChange={setLogsPageSize}
                        />
                      )}
                    </>
                  )}
                </TabsContent>

                {/* Analytics Tab */}
                <TabsContent value="analytics" className="space-y-3">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <Card className="p-3">
                      <div className="text-2xl font-bold text-pink-600">{pendingCount}</div>
                      <div className="text-xs text-muted-foreground">Pending</div>
                    </Card>
                    <Card className="p-3">
                      <div className="text-2xl font-bold text-blue-600">{upcomingCount}</div>
                      <div className="text-xs text-muted-foreground">Upcoming</div>
                    </Card>
                    <Card className="p-3">
                      <div className="text-2xl font-bold text-green-600">{logs.filter(l => l.status === 'success').length}</div>
                      <div className="text-xs text-muted-foreground">Sent</div>
                    </Card>
                    <Card className="p-3">
                      <div className="text-2xl font-bold text-red-600">{logs.filter(l => l.status === 'failed').length}</div>
                      <div className="text-xs text-muted-foreground">Failed</div>
                    </Card>
                  </div>
                  <Alert>
                    <BarChart3 className="h-4 w-4" />
                    <AlertDescription>
                      Configure message templates and automation in{" "}
                      <Link href="/settings?tab=sms-settings&smsTab=sentimentals" className="underline hover:no-underline">
                        Settings
                      </Link>.
                    </AlertDescription>
                  </Alert>
                </TabsContent>
              </Tabs>
            </CardContent>
          </CollapsibleContent>
        </Collapsible>
      </Card>

      {/* Manual Sentimental Dialog */}
      <SendManualSentimentalDialog
        open={showManualDialog}
        onOpenChange={setShowManualDialog}
      />

      {/* Edit Message Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <ResponsiveDialogContent size="md">
          <DialogHeader>
            <DialogTitle>Edit Message</DialogTitle>
            <DialogDescription>
              Customize the message before sending
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="leadName">Lead</Label>
              <Input
                id="leadName"
                value={selectedSchedule?.leadName || ""}
                disabled
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="editMessage">Message</Label>
              <Textarea
                id="editMessage"
                value={editedMessage}
                onChange={(e) => setEditedMessage(e.target.value)}
                rows={6}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveEdit}>Save Changes</Button>
          </DialogFooter>
        </ResponsiveDialogContent>
      </Dialog>
    </>
  );
};
